import React, { useState } from 'react';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { Helmet } from 'react-helmet';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const { resetPassword } = useEnhancedAuth();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage('Invalid email format. Please enter a valid email address.');
      return;
    }

    try {
      await resetPassword(email);
      setStatus('success');
      setMessage('Password reset email sent. Please check your inbox.');
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setStatus('error');
      if (error instanceof Error) {
        switch (error.message) {
          case 'Firebase: Error (auth/user-not-found).':
            setMessage('No account found with this email. Please check your email or sign up.');
            break;
          case 'Firebase: Error (auth/invalid-email).':
            setMessage('Invalid email format. Please enter a valid email.');
            break;
          default:
            setMessage('An error occurred. Please try again.');
        }
      } else {
        setMessage('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Reset Password | Social Work Test Prep Academy</title>
        <meta name="description" content="Secure password reset for your ASWB exam prep account. Quickly regain access to your adaptive learning platform." />
        <meta property="og:title" content="Reset Password | Social Work Test Prep Academy" />
        <meta property="og:description" content="Secure password reset for ASWB exam prep account." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up opacity-0">
          <h1 className="heading-1 mb-3">
            Reset Password
          </h1>
          <p className="body-base text-neutral-600">
            Enter your email address and we'll send you a secure link to reset your password.
          </p>
        </div>

        {/* Reset Form */}
        <div className="bg-neutral-50 rounded-xl p-8 shadow-ledge border border-neutral-200 animate-fade-in-up opacity-0 delay-100">
          <form onSubmit={handleResetPassword} className="space-y-6">
            {status === 'success' && (
              <div className="card-soft bg-success-light border-l-4 border-l-success animate-fade-in" role="alert">
                <p className="text-sm text-neutral-700">{message}</p>
              </div>
            )}
            {status === 'error' && (
              <div className="card-soft bg-error-light border-l-4 border-l-error animate-fade-in" role="alert">
                <p className="text-sm text-neutral-700">{message}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="text-sm font-medium text-neutral-700 block mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-base"
              />
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              className="btn-primary w-full"
            >
              {status === 'loading' ? <LoadingSpinner /> : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;